import DocumentPreview from '@/components/shared/DocumentPreview';
import en from '@/translations/en.json';

export const documentPreviewMixin = {
  methods: {
    canPreview(document) {
      // We only support PDFs for now
      return (
        (document.Documentfile && document.docType === 'pdf') ||
        document.docType === 'fa fa-file-pdf-o' ||
        document.docType === 'fa fa-file-photo-o'
      );
    },
    openPreview(document) {
      this.$modal.show(
        DocumentPreview,
        {
          document
        },
        {
          draggable: true,
          resizable: true,
          adaptive: true,
          width: '45%',
          height: '90%',
          minWidth: 300,
          maxWidth: 1920,
          minHeight: 480,
          maxHeight: 900
        }
      );
    }
  }
};

// The AI Matcher section is English-only by product decision, regardless of the event's
// languageId (which is what drives i18n.locale everywhere else — see CurrentEvent/changeLanguage).
// A component-local vue-i18n instance pinned to 'en' makes every $t() in the component resolve
// against en.json; a key it does not carry still falls back to the root instance.
//
// `sync: false` is load-bearing: by default a component-local instance mirrors the root's locale,
// which would immediately overwrite 'en' with the event language. English would still come out —
// but only via fallbackLocale, and vue-i18n logs a "Fall back to translate" warning per key.
//
// Only apply this to components reached exclusively through ParticipantDashboardLayout. Anything
// also rendered by the lobby (BookMeetingModal, SlotPicker) must keep following the event language
// there, so those take an `english` prop instead.
export const englishOnlyI18nMixin = {
  i18n: { locale: 'en', sync: false, messages: { en } }
};

export const togglesSupportChat = {
  data() {
    return {
      chatWidgetLoaded: false
    };
  },
  created() {
    window.LiveChatWidget.on('ready', () => {
      console.log('LiveChatInc widget has loaded');
      this.chatWidgetLoaded = true;
    });
    // Reflect open / closed state into a body class so the global
    // stylesheet can constrain the LiveChat iframe to a modal
    // shape (instead of the default fullscreen on mobile) and
    // render a backdrop behind it.
    window.LiveChatWidget.on('visibility_changed', (data) => {
      if (!data) return;
      if (data.visibility === 'maximized') {
        document.body.classList.add('chat-modal-open');
        this.attachChatBackdropClose();
      } else {
        document.body.classList.remove('chat-modal-open');
        this.detachChatBackdropClose();
      }
    });
  },
  beforeDestroy() {
    this.detachChatBackdropClose();
  },
  methods: {
    toggleLiveChatWindow() {
      // If we call this method before the chat widget has loaded, it throws an exception
      if (!this.chatWidgetLoaded) return;

      return window.LiveChatWidget.get('state').visibility === 'maximized'
        ? window.LiveChatWidget.call('minimize')
        : window.LiveChatWidget.call('maximize');
    },
    // ── Click-outside close ─────────────────────────────────────
    // The chat widget's modal sits over a dimmed backdrop. Tapping
    // anywhere outside the LiveChat container minimises the chat
    // (since the iframe itself is third-party we can't add a close
    // button inside it — this gives users a way out).
    attachChatBackdropClose() {
      if (this._chatBackdropHandler) return;
      this._chatBackdropHandler = (e) => {
        const target = e.target;
        if (!target) return;
        // The LiveChat widget injects a few possible containers /
        // iframes. Treat a click that lands inside ANY of them as
        // a click "inside the chat" — anything else closes it.
        const insideChat =
          (target.closest &&
            (target.closest('#chat-widget-container') ||
              target.closest('#chat-widget') ||
              target.closest('iframe[id^="livechat-"]') ||
              target.closest('iframe[id*="chat-widget"]') ||
              target.closest('iframe[title*="LiveChat" i]') ||
              target.closest('iframe[title*="Live chat" i]'))) ||
          (target.id &&
            (target.id === 'chat-widget-container' ||
              target.id === 'chat-widget' ||
              target.id.indexOf('livechat-') === 0 ||
              target.id.indexOf('chat-widget') !== -1));
        if (insideChat) return;
        if (window.LiveChatWidget && window.LiveChatWidget.call) {
          window.LiveChatWidget.call('minimize');
        }
      };
      // Pointerdown so the close fires before any focus / click on
      // page elements steals the interaction.
      document.addEventListener('pointerdown', this._chatBackdropHandler, true);
    },
    detachChatBackdropClose() {
      if (this._chatBackdropHandler) {
        document.removeEventListener(
          'pointerdown',
          this._chatBackdropHandler,
          true
        );
        this._chatBackdropHandler = null;
      }
    }
  }
};
